import {getItem, getJsonItem, setItem} from "../device/localStorage";
import {currentlySelectedMidiInputChanged} from "../eventBus/settings/settingsEventBus";
import IUserPreferencesForControlBar from "../../models/view/lessonPage/IUserPreferencesForControlBar";
import {SegmentEventTypes} from "../eventBus/segment/SegmentEventTypes";
import {subscribeToSegmentEvents} from "../eventBus/segment/segmentEventBus";
import {settingsStore} from "../../redux/settings/settingsStore";
import {lessonLocalStorage} from "../lesson/lessonLocalStorage";
import {MidiDeviceSettings} from "../../models/view/lessonPage/MidiDeviceSettings";
import {keyboardKeyCounts} from "../../redux/settingsPage/settingsPageTypes";
import {localStorageService} from "../storage/local-storage";
import storageKeys from "../storage/storageKeys";
import {partiallySetUserPreferencesForControlBar} from "services/UserPreferencesForControlBarService";

export interface ISegmentSettings{
  segmentId: string;
  shouldHideSegmentDescription: boolean;
}

export interface ISegmentsSettings{
  segments: ISegmentSettings[];
}

const defaultKeyboardKeysSettings = {
  numberOfKeys: 88,
  lowestMidiNote: 21, // A0
  highestMidiNote: 108 // C8
};

const predefinedKeyboardKeysSettings = [
  {numberOfKeys: 25, lowestMidiNote: 48, highestMidiNote: 72}, // C3 - B4
  {numberOfKeys: 49, lowestMidiNote: 36, highestMidiNote: 84}, // C2 - C6
  {numberOfKeys: 61, lowestMidiNote: 36, highestMidiNote: 96}, // C2 - C7
  {numberOfKeys: 76, lowestMidiNote: 28, highestMidiNote: 103}, // E1 - G7
  defaultKeyboardKeysSettings, // A0 - C8
];

class SettingsService {
  unsubscribeFuncs: (() => void)[];

  constructor() {
    this.unsubscribeFuncs = [
      subscribeToSegmentEvents(this.handleSegmentEvent.bind(this)),
    ];
  }

  destroy(){
    this.unsubscribeFuncs.forEach(f => f());
  }

  handleSegmentEvent(message: SegmentEventTypes){
    switch (message.type) {
      case "lessonLoaded":
        this.partiallySetUserPreferencesForControlBar({isBackingTrackEnabled: true});
    }
  }

  dispatchCurrentlySelectedLibrary(){
    const library = this.getCurrentlySelectedSoundLibrary();
    settingsStore.setCurrentlySelectedSoundLibrary(library);
  }

  //due to midi device access being async, and circular dependency of this module,
  async dispatchLocalStorageStateToReduxStore(){
    this.dispatchCurrentlySelectedLibrary();
  }

  setCurrentlySelectedMidiDevice(deviceId: string, deviceName: string) {
    localStorageService.setItem<string>(storageKeys.settings_selectedMidiDeviceId, deviceId);
    localStorageService.setItem<string>(storageKeys.settings_selectedMidiDeviceName, deviceName);
    settingsStore.setSelectedMidiDevice(deviceId, deviceName);
    currentlySelectedMidiInputChanged(deviceId, deviceName);
  }

  getCurrentlySelectedMidiDeviceId(): string {
    return getItem(storageKeys.settings_selectedMidiDeviceId) || "";
  }

  changeVirtualPianoNumberOfKeys(count: number) {
    // Guard against invalid counts
    const keyboardKeysSettings = predefinedKeyboardKeysSettings.find(k => k.numberOfKeys === count);
    if(!keyboardKeysSettings) {
      throw new Error(`No settings could be found for that count: ${count}`);
    }

    const selectedMidiDeviceId = settingsStore.getSelectedMidiDeviceId();
    const selectedMidiDeviceName = settingsStore.getSelectedMidiDeviceName();

    // Update local storage
    this.changeMidiDeviceSettings({
      id: selectedMidiDeviceId,
      name: selectedMidiDeviceName,
      numberOfKeys: count,
      dateTimeConfiguredMS: (new Date()).getTime(),
      lowestNote: keyboardKeysSettings.lowestMidiNote,
      highestNote: keyboardKeysSettings.highestMidiNote,
    });

    // Update redux
    settingsStore.setVirtualPianoNumberOfKeys(count);
  }

  getKeyboardKeysSettings(){
    let keyboardSettings = lessonLocalStorage.getMidiDeviceSettingsById(settingsStore.getSelectedMidiDeviceId());
    if(!keyboardSettings) {
      const defaultKeyboardKeysSettings = this.getDefaultKeyboardKeysSettings();
      keyboardSettings = defaultKeyboardKeysSettings;
      this.changeMidiDeviceSettings(keyboardSettings);
    }
    return keyboardSettings;
  }

  getDefaultKeyboardKeysSettings() {
    return new MidiDeviceSettings({
      id: "Unknown",
      name: "Unknown",
      numberOfKeys: defaultKeyboardKeysSettings.numberOfKeys,
      dateTimeConfiguredMS: (new Date()).getTime(),
      lowestNote: defaultKeyboardKeysSettings.lowestMidiNote,
      highestNote: defaultKeyboardKeysSettings.highestMidiNote,
    });
  }

  changeMidiDeviceSettings(midiDeviceSettings: MidiDeviceSettings){
    let numberOfKeys = midiDeviceSettings.numberOfKeys;
    numberOfKeys = keyboardKeyCounts.includes(numberOfKeys as number) ? numberOfKeys : "Custom";

    lessonLocalStorage.upsertMidiDeviceSettings({
      id: midiDeviceSettings.id,
      name: midiDeviceSettings.name,
      numberOfKeys: numberOfKeys,
      dateTimeConfiguredMS: (new Date()).getTime(),
      lowestNote: midiDeviceSettings.lowestNote,
      highestNote: midiDeviceSettings.highestNote,
    });
  }

  getCurrentlySelectedSoundLibrary(){
    if(getItem(storageKeys.currentlySelectedSoundLibrary) !== "Legacy 44.1k"){
      setItem(storageKeys.currentlySelectedSoundLibrary, "Legacy 44.1k");
      return "Legacy 44.1k";
    }
    return getItem(storageKeys.currentlySelectedSoundLibrary) || "Legacy 44.1k";
  }

  getSegmentsSettings(){
    let segmentsSettings = localStorageService.getJsonItem<ISegmentsSettings>(storageKeys.lessonPlayer_segmentsSettings);
    if(!segmentsSettings){
      segmentsSettings = {segments:[]};
      this.setSegmentsSettings(segmentsSettings);
    }
    return segmentsSettings;
  }

  getShouldHideSegmentDescription(segmentId: string){
    const segmentSetting = this.getSegmentsSettings().segments.find(s=> s.segmentId === segmentId);
    return segmentSetting?.shouldHideSegmentDescription ?? false;
  }

  setSegmentsSettings(segmentsSettings: ISegmentsSettings){
    localStorageService.setJsonItem<ISegmentsSettings>(storageKeys.lessonPlayer_segmentsSettings, segmentsSettings);
  }

  setShouldHideSegmentDescription(segmentId: string, shouldHideSegmentDescription: boolean) {
    const segmentsSettings = this.getSegmentsSettings();
    let segmentSettings = segmentsSettings.segments.find(s => s.segmentId === segmentId);
    if (!segmentSettings) {
      segmentSettings = {segmentId, shouldHideSegmentDescription};
      segmentsSettings.segments.push(segmentSettings);
    } else {
      segmentSettings.shouldHideSegmentDescription = shouldHideSegmentDescription;
    }
    this.setSegmentsSettings(segmentsSettings);
  }

  partiallySetUserPreferencesForControlBar(partialUserPreferencesForControlBar: Partial<IUserPreferencesForControlBar>){
    partiallySetUserPreferencesForControlBar(partialUserPreferencesForControlBar);
  }

  getHasDashboardModalBeenDisplayed = () => !!getJsonItem<boolean>(storageKeys.dashboard_hasModalBeenDisplayed);
  changeHasDashboardModalBeenDisplayed = (hasBeenDisplayed: boolean) => setItem(storageKeys.dashboard_hasModalBeenDisplayed, hasBeenDisplayed);

  getHasBootcampModalBeenDisplayed(){
    return getJsonItem<boolean>(storageKeys.bootcamp_hasModalBeenDisplayed) ? true : false;
  }

  changeHasBootcampModalBeenDisplayed(hasBeenDisplayed: boolean){
    setItem(storageKeys.bootcamp_hasModalBeenDisplayed, hasBeenDisplayed);
  }

  getHasCoursesModalBeenDisplayed = () => getJsonItem<boolean>(storageKeys.courses_hasModalBeenDisplayed) ? true : false;
  changeHasCoursesModalBeenDisplayed = (hasBeenDisplayed: boolean) => setItem(storageKeys.courses_hasModalBeenDisplayed, hasBeenDisplayed);

  /**
   * Usages
   * - hasSongModalBeenDisplayed() - returns true or false, to answer the question
   * - hasSongModalBeenDisplayed(true) - sets the value to true, to indicate that the modal HAS been displayed.
   * - hasSongModalBeenDisplayed(false) - sets the value to false, indicating that the modal has NOT been displayed.
   */
  hasSongModalBeenDisplayed = (hasIt?: boolean) => hasBeenDisplayed("hasSongModalBeenDisplayed", hasIt);

  markCourseModalForCourseIdAsDisplayed = (courseId: number) => {
    let coursesThatHaveBeenDisplayed = getJsonItem<number[]>(storageKeys.displayedCourseModals);
    if(!coursesThatHaveBeenDisplayed){
      coursesThatHaveBeenDisplayed = [];
    }
    if(coursesThatHaveBeenDisplayed.indexOf(courseId) < 0){
      coursesThatHaveBeenDisplayed.push(courseId);
    }
    setItem(storageKeys.displayedCourseModals, coursesThatHaveBeenDisplayed);
  }
  getHasModalForCourseIdBeenDisplayed(courseId: number){
    const coursesThatHaveBeenDisplayed = getJsonItem<number[]>(storageKeys.displayedCourseModals);
    if(!coursesThatHaveBeenDisplayed){ return false; }
    return coursesThatHaveBeenDisplayed.indexOf(courseId) >= 0;
  }

}

/**
 * Usages
 * - hasBeenDisplayed() - returns true or false, to answer the question
 * - hasBeenDisplayed(true) - sets the value to true, to indicate that the thing HAS been displayed.
 * - hasBeenDisplayed(false) - sets the value to false, indicating that the thing has NOT been displayed.
 */
function hasBeenDisplayed(key: string, value?: boolean): boolean {
  if (value === undefined) {
    return !!getJsonItem<boolean>(key);
  }

  setItem(key, value);
  return value;
}

const settingsService = new SettingsService();
void settingsService.dispatchLocalStorageStateToReduxStore();

export default settingsService;
